import moment from 'dayjs';

export default class FormModifier {
  constructor(host) {
    this.host = host;
  }

  shorten = (str, maxLen, separator = ' ') => {
    if (str.length <= maxLen) {
      return str;
    }
    return str.substr(0, str.lastIndexOf(separator, maxLen)) + '&hellip;';
  };

  registerLinkClick = e => {
    dataLayer.push({
      event: 'Click',
      elementType: 'Event Link',
      element: e.currentTarget.querySelector('a').href,
    });
  };

  _modifyEventCards = () => {
    const eventsItems = this.host.querySelectorAll('.oa-event-result-container');

    if (!eventsItems) {
      return;
    }

    eventsItems.forEach(event => {
      const title = event.getAttribute('data-event-name');
      const url = event.querySelector('.oa-event-result-name-link').getAttribute('href');
      const type = event.querySelector('.oa-event-result-type').innerText;
      const startTime = event.querySelector('.oa-event-result-starttime').innerText;
      const endTime = event.querySelector('.oa-event-result-endtime').innerText;
      const timeZone = event.querySelector('.oa-event-result-timezone').innerText;
      const date = new Date(event.querySelector('.oa-event-result-startdate').innerText);
      const formattedDate = moment(date.toString()).format('dddd, MMMM D, YYYY');
      const description = event.querySelector('.oa-event-result-description')
        ? event.querySelector('.oa-event-result-description').innerText
        : '';
      const truncatedDescription = description ? this.shorten(description, 110) : '';
      const recommended = ['THL Virtual Action', 'NCMC', 'Virtual Art Activism'].includes(type);
      const inperson = ['In-Person COVID Waiv'].includes(type);
      const location = event.querySelector('.oa-event-result-location').innerText;

      const eventLink = document.createElement('a');
      eventLink.setAttribute('href', url);
      eventLink.setAttribute('target', '_blank');
      eventLink.setAttribute('rel', 'noopener noreferrer');

      const eventHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">
          <div class="ea-event__content">
            ${recommended ? '<div class="ea-event__recommended">Recommended</div>' : ''}
            ${inperson ? '<div class="ea-event__inperson">In-Person</div>' : ''}
            <h3 class="ea-event__title">${title}</h3>
            <p class="ea-event__description" title="${description}">${truncatedDescription}</p>
            <ul class="ea-event__details">
              <li class="ea-event__detail ea-event__detail--location">
                ${location.startsWith('http') ? 'Online' : location}
              </li>
              <li class="ea-event__detail ea-event__detail--date">
                <time datetime="${date.toISOString()}">${formattedDate}</time>
              </li>
              <li class="ea-event__detail ea-event__detail--time">
                From ${startTime} to ${endTime} ${timeZone}
              </li>
            </ul>
          </div>
        </a>`;
      event.innerHTML = eventHtml;
      event.addEventListener('click', this.registerLinkClick);
      event.classList.add('ea-event');
    });
  };

  _wrapEventsList = () => {
    const eventContainer = this.host.querySelector('.oa-event-list-results-container');
    const eventsList = document.createElement('div');
    const eventsItems = this.host.querySelectorAll('.oa-event-result-container');
    const resultTitle = eventContainer.querySelector('h3');
    const map = eventContainer.querySelector('.oa-map-container');

    if (!eventsItems.length) {
      return;
    }

    eventsItems.forEach(item => {
      eventsList.appendChild(item);
    });

    eventsList.classList.add('ea-events-list');

    eventContainer.prepend(eventsList);
    if (map) {
      eventContainer.prepend(map);
    }
    eventContainer.prepend(resultTitle);
  };

  _inputToButton = () => {
    const input = this.host.querySelector('#EventListSubmit');
    const inputContainer = input.parentNode;
    const label = input.value;

    inputContainer.innerHTML = `<button type="submit" id="EventListSubmit">${label}</button>`;
  };

  // Injects link that will scroll the browser to the end of the EA event list
  _injectScrollLink = () => {
    const eventsListResultsContainer = this.host.querySelector('.oa-event-list-results-container');
    const target = document.createElement('span');
    const readMore = document.createElement('div');
    const readMoreLink = document.createElement('a');
    readMore.classList.add('read-more');
    readMoreLink.classList.add('read-more__link');
    readMoreLink.setAttribute('href', '#');

    readMoreLink.innerText = 'Read more about our event types below';

    readMore.append(readMoreLink);
    eventsListResultsContainer.prepend(readMore);
    eventsListResultsContainer.append(target);

    readMoreLink.addEventListener('click', e => {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth' });
    });
  };

  _quickFilters = () => {
    // EA elements we need
    const eaSubmitButton = this.host.getElementById('EventListSubmit');
    const eaInputContainer = this.host.querySelector('.oa-input-container');
    const eaSelect = this.host.getElementById('EventTypes');
    const eaSelectOptions = eaSelect.querySelectorAll('option');
    // Create array of EA options
    const eaSelectOptionsArray = [];
    eaSelectOptions.forEach(option => {
      eaSelectOptionsArray.push(option.innerText);
    });
    const selectedOptions = Array.from(eaSelect.selectedOptions).map(v => v.text);

    const quickFilterOptions = [
      {
        label: 'I’m new! Help me get started.',
        text: ['National Changemaker Call', 'Virtual Education', 'NCMC'],
      },
      {
        label: 'I want to make a difference.',
        text: ['THL Virtual Action', 'Virtual Art Activism', 'Virtual Take Action', 'In-Person COVID Waiv'],
      },
      {
        label: 'I want to make friends.',
        text: ['THL Virtual Social', 'Virtual Social', 'Virtual Book Club'],
      },
    ];

    const quickFiltersTitle = document.createElement('h3');
    quickFiltersTitle.innerText = 'What are you looking to do?';
    const checkBoxRow = document.createElement('div');
    checkBoxRow.classList.add('quick-filter-container');

    quickFilterOptions.forEach(option => {
      // Remove options that aren't in EA for checkbox matching purposes
      const filteredOptions = option.text.filter(i => eaSelectOptionsArray.includes(i));
      // Create DOM Elements;
      const checkBoxLabel = document.createElement('label');
      checkBoxLabel.classList.add('at-check');
      // Input
      const checkboxInput = document.createElement('input');
      checkboxInput.setAttribute('type', 'checkbox');
      checkboxInput.setAttribute('name', 'quick-filter');
      checkboxInput.setAttribute('data-values', filteredOptions.toString());
      // Input title
      const checkboxTitleContainer = document.createElement('span');
      checkboxTitleContainer.classList.add('at-checkbox-title-container');
      const checkboxTitle = document.createElement('span');
      checkboxTitle.classList.add('at-checkbox-title');
      checkboxTitle.innerText = option.label;

      // Check box on load if all options are true
      checkboxInput.checked =
        checkboxInput.dataset.values && filteredOptions.every(i => selectedOptions.includes(i));

      // Add elements to page
      checkboxTitleContainer.append(checkboxTitle);
      checkBoxLabel.append(checkboxInput);
      checkBoxLabel.append(checkboxTitleContainer);
      checkBoxRow.append(checkBoxLabel);

      checkboxInput.addEventListener('change', e => {
        const selectedOptions = this.host.querySelectorAll('input[name="quick-filter"]:checked');
        let selectedOptionsArray = [];

        // Add options to array
        selectedOptions.forEach(so => {
          selectedOptionsArray = selectedOptionsArray.concat(so.dataset.values.split(','));
        });

        /*  Remove explicitly deselected options:
            This prevents unexpected behavior of unchecking a checkbox who's
            requirments for being checked are met by other filters */
        if (!e.currentTarget.checked) {
          selectedOptionsArray = selectedOptionsArray.filter(
            soaItem => !e.currentTarget.dataset.values.split(',').includes(soaItem),
          );
        }

        // (De)select appropriate options
        eaSelectOptions.forEach(o => {
          o.selected = selectedOptionsArray.includes(o.text);
        });

        // Submti form via button click in case EA uses that event for anything
        eaSubmitButton.click();
      });
    });
    // eaInputContainer.parentNode.insertBefore(checkBoxRow, eaInputContainer);
    eaInputContainer.prepend(checkBoxRow);
    eaInputContainer.prepend(quickFiltersTitle);
  };

  _scrolltoEventsList = () => {
    const eventForm = this.host.querySelector('.form--event');
    eventForm.scrollIntoView({ behavior: 'smooth' });
  };

  populate = () => {
    this._modifyEventCards();
    this._wrapEventsList();
    this._inputToButton();
    this._injectScrollLink();
    this._quickFilters();
    this._scrolltoEventsList();
  };

  createObserver = () => {
    const pageContent = this.host.querySelector('.page-content');

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          this.populate();
        }
      });
    });

    observer.observe(pageContent, {
      childList: true,
    });
  };
}
